// Compliance module interfaces

/** Matches backend compliance status enum (`COMPLIANT`, …). */
export const COMPLIANCE_STATUSES = [
  "COMPLIANT",
  "NON_COMPLIANT",
  "PARTIALLY_COMPLIANT",
  "PENDING",
  "IN_PROGRESS",
  "EXPIRED",
  "EXEMPT",
  "UNDER_REVIEW",
] as const;

export type TComplianceStatus = (typeof COMPLIANCE_STATUSES)[number];

export const COMPLIANCE_STATUS_LABELS: Record<TComplianceStatus, string> = {
  COMPLIANT: "Compliant",
  NON_COMPLIANT: "Non-compliant",
  PARTIALLY_COMPLIANT: "Partially compliant",
  PENDING: "Pending",
  IN_PROGRESS: "In progress",
  EXPIRED: "Expired",
  EXEMPT: "Exempt",
  UNDER_REVIEW: "Under review",
};
export type TComplianceCategory =
  | "regulatory"
  | "institutional"
  | "accreditation"
  | "other";
export type TComplianceFrequency =
  | "annual"
  | "biannual"
  | "quarterly"
  | "monthly"
  | "weekly"
  | "custom";

export type TCompliance = {
  id: string;
  name: string;
  category: TComplianceCategory;
  status: TComplianceStatus;
  dueDate?: string; // ISO date string
  description?: string;
  frequency?: TComplianceFrequency;
  instituteId: string;
};

export type TCreateComplianceRequest = {
  name: string;
  category: TComplianceCategory;
  description?: string;
  frequency?: TComplianceFrequency;
  dueDate?: string;
  status?: TComplianceStatus;
  instituteId: string;
};

export type TUpdateComplianceRequest = {
  name?: string;
  category?: TComplianceCategory;
  description?: string;
  frequency?: TComplianceFrequency;
  dueDate?: string;
  status?: TComplianceStatus;
};

export type TComplianceTemplate = {
  id: string;
  name: string;
  description?: string;
  category: TComplianceCategory;
  compliances: Omit<TCreateComplianceRequest, "instituteId">[];
};

export type TApplyTemplateRequest = {
  templateId: string;
  instituteId: string;
  customizations?: Record<
    string,
    Partial<Omit<TCreateComplianceRequest, "instituteId">>
  >;
};

/** Wire shapes for GET `/v1/settings/compliance` (OpenAPI `ComplianceListWireDto`). */
export type TComplianceListItemWireDto = {
  id: string;
  code: string;
  title: string;
  description?: string;
  category: string;
  subCategory?: string;
  type: string;
  status: string;
  riskLevel?: string;
  priority?: string;
  jurisdiction?: string;
  authority?: string;
  dueDate?: string;
  nextDueDate?: string;
  lastCompletedAt?: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
};

export type TComplianceListWireDto = {
  success: boolean;
  data: TComplianceListItemWireDto[];
  count: number;
  page: number;
  limit: number;
};

/** POST `/v1/settings/compliance` body (`CreateComplianceItemBodyDto`). */
export type TCreateComplianceItemBodyDto = {
  title: string;
  instituteId: string;
  description?: string;
  code?: string;
  category?: string;
  subCategory?: string;
  type?: string;
  status?: string;
  riskLevel?: string;
  priority?: string;
  jurisdiction?: string;
  authority?: string;
  effectiveDate?: string;
  dueDate?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
};

export type TComplianceIdAckDto = {
  success: boolean;
  data: { id: string };
};

export type TComplianceUpdateAckDto = {
  success: boolean;
  data?: { id: string };
  message?: string;
};
